<template lang="pug">
.study-candidate
  //- サイドメニュー部
  side-menu.study-candidate__side-menu(
    :userRole='userRole',
    :currentLink='currentLink',
    :linkEvent='sideMenuLinkEvent',
    :logoutEvent='logoutEvent'
  )
    template(#separateText)
      slot(name='sideMenuSeparateText')
    template(#logoutText)
      slot(name='logoutText')
  //- コンテンツ表示部
  .study-candidate__content
    breadcrumb-list.study-candidate__content__breadcrumb(:breadcrumbs='breadcrumbs', :moveEvent='breadcrumbMoveEvent')
    .study-candidate__content__body
      .study-candidate__content__body__heading
        tab-heading.study-candidate__content__body__heading__tab(:isActive='true')
          slot(name='tabLabel')
      .study-candidate__content__body__main
        .study-candidate__content__body__main__action
          search-student.study-candidate__content__body__main__action__search(
            :userRole='userRole',
            :gradeSelectOptions='gradeSelectOptions',
            :gradeValue='gradeValue',
            :fullName='fullName',
            :academyName='academyName',
            :branchName='branchName',
            :status='status',
            :fullNamePlaceholder='fullNamePlaceholder',
            :academyNamePlaceholder='academyNamePlaceholder',
            :branchNamePlaceholder='branchNamePlaceholder',
            :statusPlaceholder='statusPlaceholder',
            :gradeChangeEvent='setGradeValue',
            :fullNameChangeEvent='setFullName',
            :academyNameChangeEvent='setAcademyName',
            :branchNameChangeEvent='setBranchName',
            :statusChangeEvent='setStatus',
            :searchEvent='searchEvent'
          )
            template(#searchButtonText)
              slot(name='searchButtonText')
          .study-candidate__content__body__main__action__button
            button-icon-circle.study-candidate__content__body__main__action__button__icon(
              type='download',
              @click='downloadEvent'
            )
              slot(name='downloadIconLabel')
            button-upload.study-candidate__content__body__main__action__button__icon(
              v-if='isAdmin',
              :uploadEvent='uploadEvent'
            )
              template(#iconLabel)
                slot(name='uploadIconLabel')
        study-candidate-table.study-candidate__content__body__main__table(
          v-if='sort && studentDatas',
          :userRole='userRole',
          :sort='sort',
          :sortChangeEvent='changeSort',
          :studentDatas='studentDatas'
        )
          template(#userNameHeader)
            slot(name='userNameHeader')
          template(#bcbtCodeHeader)
            slot(name='bcbtCodeHeader')
          template(#academyNameHeader)
            slot(name='academyNameHeader')
          template(#branchNameHeader)
            slot(name='branchNameHeader')
          template(#gradeNameHeader)
            slot(name='gradeNameHeader')
          template(#statusNameHeader)
            slot(name='statusNameHeader')
          template(#possibilityHeader)
            slot(name='possibilityHeader')
        .study-candidate__content__body__main__footer
          button-pagination.study-candidate__content__body__main__footer__pager(
            :status='pagenationStatus',
            @click='pagenationEvent'
          )
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import SideMenu from '@/components/organisms/manager/SideMenu.vue'
import BreadcrumbList from '@/components/organisms/manager/BreadcrumbList.vue'
import TabHeading from '@/components/molecules/manager/TabHeading.vue'
import SearchStudent from '@/components/organisms/manager/SearchStudent.vue'
import ButtonIconCircle from '@/components/molecules/manager/ButtonIconCircle.vue'
import ButtonUpload from '@/components/molecules/manager/ButtonUpload.vue'
import StudyCandidateTable from '@/components/organisms/manager/StudyCandidateTable.vue'
import ButtonPagination from '@/components/molecules/manager/ButtonPagination.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudentListType, StudentListSortType } from '@/models/studentListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { SortSetting } from '@/const/SortSettings'

/**
 * テスト受験可能なユーザー画面
 */
@Component({
  components: {
    SideMenu,
    BreadcrumbList,
    TabHeading,
    SearchStudent,
    ButtonIconCircle,
    ButtonUpload,
    StudyCandidateTable,
    ButtonPagination,
  },
})
export default class StudyCandidate extends Vue {
  // ユーザ権限
  @Prop()
  userRole!: UserRoles

  // このページのリンク設定
  @Prop()
  currentLink!: LinkSetting

  // サイドメニューリンク押下時のイベント
  @Prop()
  sideMenuLinkEvent!: Function

  // ログアウトボタン押下時のイベント
  @Prop()
  logoutEvent!: Function

  // パンくず一覧情報
  @Prop()
  breadcrumbs!: breadcrumbType[]

  // パンくず遷移イベント
  @Prop()
  breadcrumbMoveEvent!: Function

  // 学年選択オプション
  @Prop()
  gradeSelectOptions!: InputItem[]

  // 氏名プレースホルダー
  @Prop()
  fullNamePlaceholder!: string

  // 塾名プレースホルダー（ユーザ権限がシステム管理者の場合のみ設定）
  @Prop()
  academyNamePlaceholder?: string

  // 教室名プレースホルダー(ユーザ権限がシステム管理者、塾管理者の場合のみ設定)
  @Prop()
  branchNamePlaceholder?: string

  // ステータスプレースホルダー
  @Prop()
  statusPlaceholder!: string

  // 検索イベント
  @Prop()
  searchEvent!: Function

  // ダウンロードボタン押下時イベント
  @Prop()
  downloadEvent!: Function

  // アップロードボタン押下時イベント（ユーザ権限がシステム管理者の場合のみ設定）
  @Prop()
  uploadEvent?: Function

  // ソート値（項目毎の昇順フラグ）
  @Prop()
  sort!: StudentListSortType

  // 生徒一覧情報
  @Prop()
  studentDatas!: StudentListType[]

  // ページネーションの状態
  @Prop()
  pagenationStatus!: PaginationStatus

  // ページネーションイベント
  @Prop()
  pagenationEvent!: Function

  // === 選択値を保持する変数群
  // 学年
  public gradeValue = ''
  // 氏名
  public fullName = ''
  // 塾名
  public academyName = ''
  // 教室名
  public branchName = ''
  // ステータス
  public status = ''

  // === 選択値をセットする関数群
  // 学年をセット
  private setGradeValue(value: string) {
    this.gradeValue = value
  }
  // 氏名をセット
  private setFullName(value: string) {
    this.fullName = value
  }
  // 塾名をセット
  private setAcademyName(value: string) {
    this.academyName = value
  }
  // 教室名をセット
  private setBranchName(value: string) {
    this.branchName = value
  }
  // ステータスをセット
  private setStatus(value: string) {
    this.status = value
  }

  // ソート値を変更
  private changeSort(sortSetting: SortSetting, sortTarget: string) {
    const sort = this.sort
    sort[sortTarget as keyof StudentListSortType] = sortSetting
    this.$emit('sort-changed', sort)
  }

  // システム管理者であるか
  private get isAdmin(): boolean {
    return this.userRole === UserRoles.ADMIN
  }
}
</script>

<style lang="stylus" scoped>
.study-candidate
  display flex
  width 100%
  height 100%

  &__content
    width 100%
    height 100%
    display flex
    flex-direction column
    padding 40px
    gap 20px
    overflow-y auto

    &__body
      display flex
      flex-direction column

      &__heading
        display flex
        height 43px

      &__main
        z-index 1
        width 100%
        min-height 500px
        display flex
        flex-direction column
        border-radius 0px 15px 15px 15px
        background var(--color-manager-list__background)
        box-shadow 0px 3px 5px 0px var(--color-manager-list__shadow)

        &__action
          display flex
          justify-content space-between
          align-items center
          padding 12px 20px

          &__search
            padding 7px 5px

          &__button
            display flex
            gap 12px

            &__icon
              width 32px
              height 32px

        &__footer
          display flex
          justify-content flex-end
          align-items center
          padding 20px 30px
          margin-top auto
</style>
